/**
 * useActiveUser — single source of truth for "who is using the app right now".
 *
 * Returns:
 *   userId         — Cognito sub if authed, anon-${uuid} if anonymous, null while loading
 *   isAnonymous    — true when userId starts with 'anon-'
 *   isAuthed       — true when userId is a Cognito sub
 *   isLoading      — true while Amplify bootstrap is in flight
 *
 * Behaviour:
 *   - On mount: race fetchAuthSession against a 4s timeout. Resolved tokens →
 *     authed; timeout/no-tokens → look up or create the anon UUID in Dexie meta.
 *   - Subscribes to Amplify Hub auth events; updates userId on signedIn / signedOut.
 *
 * Canonical: shared-docs/offline-first-architecture.md
 */

import { useEffect, useState, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { withTimeout, AUTH_TIMEOUT_MS } from '../services/network';
import { getOrCreateAnonUserId } from '../services/db/meta';

async function resolveUser() {
  try {
    const session = await withTimeout(fetchAuthSession(), AUTH_TIMEOUT_MS, 'fetchAuthSession');
    const sub = session?.tokens?.idToken?.payload?.sub;
    if (sub) {
      return { userId: String(sub), isAnonymous: false, isAuthed: true };
    }
  } catch {
    // timeout or offline — fall through to anon
  }
  const anonId = await getOrCreateAnonUserId();
  return { userId: anonId, isAnonymous: true, isAuthed: false };
}

export function useActiveUser() {
  const [state, setState] = useState({
    userId: null,
    isAnonymous: false,
    isAuthed: false,
    isLoading: true,
  });

  const refresh = useCallback(async () => {
    const resolved = await resolveUser();
    setState({ ...resolved, isLoading: false });
  }, []);

  useEffect(() => {
    let cancelled = false;
    resolveUser().then((resolved) => {
      if (!cancelled) setState({ ...resolved, isLoading: false });
    });
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload?.event === 'signedIn' || payload?.event === 'signedOut') {
        resolveUser().then((resolved) => {
          if (!cancelled) setState({ ...resolved, isLoading: false });
        });
      }
    });
    return () => {
      cancelled = true;
      try {
        unsubscribe();
      } catch {
        // Hub.listen returns a function; ignore if shape changes
      }
    };
  }, []);

  return { ...state, refresh };
}

export default useActiveUser;
